import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatProgressBarModule } from '@angular/material/progress-bar';

export interface TableColumn {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'percentage' | 'currency' | 'progress';
  format?: string;
}

@Component({
  selector: 'app-table',
  imports: [
    CommonModule,
    MatTableModule,
    MatProgressBarModule
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss'
})
export class TableComponent {
  @Input() dataSource: any[] = [];
  @Input() columns: TableColumn[] = [];
  @Input() displayedColumns: string[] = [];

  /**
   * Get column configuration by key
   */
  getColumnConfig(key: string): TableColumn | undefined {
    return this.columns.find(col => col.key === key);
  }

  /**
   * Check if column should show progress bar
   */
  isProgressBarColumn(column: string): boolean {
    const config = this.getColumnConfig(column);
    return config?.type === 'progress';
  }

  /**
   * Get progress bar color based on value
   */
  getProgressColor(value: number): 'primary' | 'accent' | 'warn' {
    if (value >= 80) return 'primary';
    if (value >= 60) return 'accent';
    return 'warn';
  }

  /**
   * Format cell value based on column type
   */
  formatCellValue(value: any, column: string): string {
    const config = this.getColumnConfig(column);

    if (!config) return value?.toString() || '';

    switch (config.type) {
      case 'percentage':
        return typeof value === 'number' ? `${value}%` : value;
      case 'currency':
        if (typeof value === 'number') {
          return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP'
          }).format(value);
        }
        return value;
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : value;
      default:
        return value?.toString() || '';
    }
  }

  /**
   * Get cell value for display
   * Rounds numeric values to whole numbers using Math.round()
   */
  getCellValue(element: any, column: string): any {
    const value = element[column];
    // Simple check: if it's a number, round it
    if (typeof value === 'number' && !isNaN(value)) {
      return Math.round(value);
    }
    return value;
  }
}
